import React from "react"
import { Badge } from "../ui/badge"
import { SvgCheckCircle } from "../icons"

interface Props {
  activeService: any

  getPrice: (amount: number, symbol: string) => string
  getUnits: (unit: string, amount: number) => string
  getServiceType: () => React.ReactNode
  getSessionCount: () => React.ReactNode
  getLengthOrExpiration: () => React.ReactNode
  formatTextWithBreaks: (text: string) => React.ReactNode

  leftColumnItems: string[]
  rightColumnItems: string[]
}

export const ServiceDetailsPanel: React.FC<Props> = ({
  activeService,
  getPrice,
  getUnits,
  getServiceType,
  getSessionCount,
  getLengthOrExpiration,
  formatTextWithBreaks,
  leftColumnItems,
  rightColumnItems,
}) => {
  if (!activeService) return null

  const billingAmount = parseInt(
    activeService?.service_details?.billing?.amount
  )

  return (
    <div className="flex w-full flex-col md:w-[60%]">
      <div
        className={`aspect-w-3 aspect-h-1 md:aspect-h-2 mx-auto mb-[32px] w-full rounded-[14px]
        ${activeService?.service_details?.image?.color}
        relative min-h-[140px]`}
      >
        {activeService?.service_details?.image?.exists && (
          <img
            src={`https://clientservice-images-${
              process.env.REACT_APP_USER_ENVIRONMENT
            }.s3.amazonaws.com/${
              activeService.stripe_product_id
            }?${new Date().getTime()}`}
            alt="service"
            className="aspect-[3/1] w-full rounded-[14px] object-cover"
          />
        )}
      </div>

      <div className="mx-auto flex w-full flex-col px-[2px]">
        <div className="mb-[20px] flex w-full items-center justify-between md:mb-[24px]">
          <h3 className="max-w-[500px] truncate text-[18px] font-bold">
            {activeService?.service_details?.title}
          </h3>

          <h3 className="text-blurple text-[22px] font-bold">
            {getPrice(billingAmount, activeService?.service_details?.symbol)}
            <span className="text-graySlate text-base font-normal">
              {getUnits(
                activeService?.service_details?.billing?.unit,
                billingAmount
              )}
            </span>
          </h3>
        </div>

        <div className="bg-grayMist mb-[16px] h-[1px] w-full" />

        <div className="relative mb-[32px] mr-auto flex w-full flex-col gap-[12px] text-base">
          <div className="absolute right-0 top-0">{getServiceType()}</div>

          {activeService?.service_details?.type !== "consultation" && (
            <>
              <div className="flex items-center gap-[12px]">
                <SvgCheckCircle />
                {getSessionCount()}
              </div>

              <div className="flex items-center gap-[12px]">
                <SvgCheckCircle />
                <div>
                  {parseInt(activeService?.service_details?.session?.duration) /
                    60}{" "}
                  minutes
                  <span className="text-darkGray"> / session</span>
                </div>
              </div>

              <div className="flex items-center gap-[12px]">
                <SvgCheckCircle />
                {getLengthOrExpiration()}
              </div>
            </>
          )}
        </div>

        <div className="bg-grayFlash mb-4 flex h-[32px] w-[142px] items-center justify-center rounded-[8px] font-bold">
          Program Details
        </div>

        {activeService?.service_details?.details?.features?.feature1 && (
          <div className="mb-[24px] mr-auto flex flex-col gap-[16px] text-base md:mb-[32px]">
            {[1, 2, 3].map((i) => {
              const feature =
                activeService?.service_details?.details?.features?.[
                  `feature${i}`
                ]
              if (!feature) return null

              return (
                <div key={i} className="flex items-center gap-[12px]">
                  <SvgCheckCircle />
                  {feature}
                </div>
              )
            })}
          </div>
        )}

        {activeService?.service_details?.details?.description1 && (
          <div className="mb-[40px] space-y-4">
            {formatTextWithBreaks(
              activeService?.service_details?.details?.description1
            )}
          </div>
        )}

        {activeService?.is_group_service && (
          <div className="mb-4 mr-auto w-full">
            <div className="bg-grayFlash mb-4 flex h-8 w-[142px] items-center justify-center gap-2 rounded-[8px] font-bold">
              Scheduled Dates
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-2">
                {leftColumnItems.map((timeStr, idx) => (
                  <Badge key={`left-${idx}`} variant="neutral">
                    {timeStr}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-col items-center gap-2">
                {rightColumnItems.map((timeStr, idx) => (
                  <Badge key={`right-${idx}`} variant="neutral">
                    {timeStr}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ServiceDetailsPanel
